import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { ExportButton } from '@/components/ExportButton'
import { QueryState } from '@/components/QueryState'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import {
  ENTRY_SOURCES,
  SOURCE_LABELS,
  closePeriod,
  exportEntries,
  formatMoney,
  listEntries,
  listPeriods,
  reopenPeriod,
  type EntrySource,
  type JournalEntrySummary,
  type JournalFilters,
  type Period,
} from '@/features/accounting/api'
import { useAuth } from '@/features/auth/store'
import { listLocations } from '@/features/orders/api'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

export function StatusPill({ status }: { status: JournalEntrySummary['status'] }) {
  const tone =
    status === 'posted'
      ? 'bg-success/10 text-success'
      : status === 'reversed'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
        : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
  const label = status === 'reversal' ? 'Reversal' : status === 'reversed' ? 'Reversed' : 'Posted'
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${tone}`}>{label}</span>
}

const columns: Column<JournalEntrySummary>[] = [
  {
    key: 'entry_number',
    header: 'Entry',
    className: 'font-mono text-xs',
    sortValue: (entry) => entry.entry_number,
    cell: (entry) => (
      <Link to={`/accounting/journal/${entry.id}`} className="hover:underline">
        {entry.entry_number}
      </Link>
    ),
  },
  {
    key: 'entry_date',
    header: 'Date',
    className: 'whitespace-nowrap text-slate-500 dark:text-slate-400',
    sortValue: (entry) => entry.entry_date,
    cell: (entry) => new Date(`${entry.entry_date}T00:00:00`).toLocaleDateString(),
  },
  {
    key: 'description',
    header: 'Description',
    className: 'font-medium',
    sortValue: (entry) => entry.description,
    cell: (entry) => entry.description,
  },
  {
    key: 'source_type',
    header: 'Raised by',
    sortValue: (entry) => entry.source_type,
    cell: (entry) => SOURCE_LABELS[entry.source_type] ?? entry.source_type,
  },
  {
    key: 'status',
    header: 'Status',
    sortValue: (entry) => entry.status,
    cell: (entry) => <StatusPill status={entry.status} />,
  },
  {
    key: 'total_debit',
    // Debits and credits are equal by construction, so one column says everything the two
    // would -- and the trial balance is where the pair belongs.
    header: 'Amount',
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    sortValue: (entry) => Number(entry.total_debit),
    cell: (entry) => formatMoney(entry.total_debit),
  },
]

/**
 * Open and closed months, with the switch that changes it.
 *
 * A period opens itself the first time something posts into it, so this list is a record of
 * what has happened rather than something to set up in advance. Closing is the deliberate
 * act -- it is what stops a late invoice landing in a month whose numbers have already
 * gone out -- so it is the only direction with a button on the happy path.
 */
function Periods() {
  const can = useAuth((s) => s.can)
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['accounting-periods'],
    queryFn: listPeriods,
    retry: false,
  })

  const toggle = useMutation({
    mutationFn: (period: Period) =>
      period.status === 'open' ? closePeriod(period.id) : reopenPeriod(period.id),
    onSuccess: (period) => {
      void queryClient.invalidateQueries({ queryKey: ['accounting-periods'] })
      toast.success(
        period.status === 'closed' ? `${period.name} closed.` : `${period.name} reopened.`,
      )
    },
    onError: (mutationError: unknown) => {
      toast.error(
        mutationError instanceof ApiError
          ? mutationError.message
          : 'Could not reach the server. Nothing was changed.',
      )
    },
  })

  const periods = data?.data ?? []
  const canManage = can('chart_of_accounts', 'update')

  return (
    <Card>
      <CardHeader
        title="Periods"
        description="A month opens itself when the first entry lands in it. Closing one refuses any further posting into it."
      />
      <QueryState
        isLoading={isLoading}
        error={error}
        isEmpty={periods.length === 0}
        emptyMessage="No period has been opened yet — nothing has posted."
      >
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {periods.map((period) => (
            <li key={period.id} className="flex items-center justify-between gap-4 px-5 py-3">
              <div>
                <p className="text-sm font-medium">{period.name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {new Date(`${period.start_date}T00:00:00`).toLocaleDateString()} –{' '}
                  {new Date(`${period.end_date}T00:00:00`).toLocaleDateString()}
                  {period.closed_at &&
                    ` · closed ${new Date(period.closed_at).toLocaleDateString()}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={
                    period.status === 'open'
                      ? 'rounded bg-success/10 px-2 py-0.5 text-xs font-medium text-success'
                      : 'rounded bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                  }
                >
                  {period.status === 'open' ? 'Open' : 'Closed'}
                </span>
                {canManage && (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={toggle.isPending && toggle.variables?.id === period.id}
                    onClick={() => toggle.mutate(period)}
                  >
                    {period.status === 'open' ? 'Close' : 'Reopen'}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </QueryState>
    </Card>
  )
}

export function JournalEntries() {
  const [filters, setFilters] = useState<JournalFilters>({ page: 1 })
  const navigate = useNavigate()
  const canPost = useAuth((s) => s.can)('journal_entries', 'create')

  const { data: locationsPage } = useQuery({
    queryKey: ['locations'],
    queryFn: () => listLocations(),
  })
  const locations = locationsPage?.data ?? []

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['journal-entries', filters],
    queryFn: () => listEntries(filters),
    retry: false,
  })

  const entries = data?.data ?? []
  const meta = data?.meta

  // Any filter change starts again at page one: page 4 of the old result set is not page 4
  // of the new one, and landing on an empty page reads as "no results".
  const set = (patch: Partial<JournalFilters>) =>
    setFilters((current) => ({ ...current, ...patch, page: 1 }))

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Journal</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {meta ? `${meta.total} entr${meta.total === 1 ? 'y' : 'ies'}` : 'Loading…'}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/accounting/trial-balance"
            className="text-sm font-medium text-brand-700 hover:underline dark:text-brand-300"
          >
            Trial balance
          </Link>
          {canPost && (
            <Button onClick={() => navigate('/accounting/journal/new')}>New entry</Button>
          )}
        </div>
      </header>

      <Card>
        <CardHeader
          title="Every entry ever posted"
          description="Nothing here can be edited or deleted, by anyone. A wrong entry is corrected with a reversal, which leaves both halves on the record."
        />
        <DataTable
          rows={entries}
          columns={columns}
          getRowId={(entry) => entry.id}
          caption="Journal entries"
          isLoading={isLoading}
          error={error}
          emptyMessage="No entries match these filters."
          toolbar={
            <div className="flex flex-wrap items-center gap-2">
              <select
                aria-label="Raised by"
                className={SELECT_CLASS}
                value={filters.source_type ?? ''}
                onChange={(event) => set({ source_type: event.target.value as EntrySource | '' })}
              >
                <option value="">Any source</option>
                {ENTRY_SOURCES.map((source) => (
                  <option key={source} value={source}>
                    {SOURCE_LABELS[source]}
                  </option>
                ))}
              </select>
              <select
                aria-label="Location"
                className={SELECT_CLASS}
                value={filters.location_id ?? ''}
                onChange={(event) => set({ location_id: event.target.value })}
              >
                <option value="">Any location</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
              <Input
                type="date"
                aria-label="Posted from"
                className="w-auto"
                value={filters.date_from ?? ''}
                onChange={(event) => set({ date_from: event.target.value })}
              />
              <Input
                type="date"
                aria-label="Posted to"
                className="w-auto"
                value={filters.date_to ?? ''}
                onChange={(event) => set({ date_to: event.target.value })}
              />
              {(filters.source_type ||
                filters.location_id ||
                filters.date_from ||
                filters.date_to) && (
                <Button variant="ghost" size="sm" onClick={() => setFilters({ page: 1 })}>
                  Clear
                </Button>
              )}
              <ExportButton
                filename="journal_entries"
                selectClassName={SELECT_CLASS}
                onExport={(format) => exportEntries(filters, format)}
                errorMessage="Could not export the journal."
              />
            </div>
          }
          search={{
            placeholder: 'Search number or description…',
            matches: (entry, query) =>
              entry.entry_number.toLowerCase().includes(query) ||
              entry.description.toLowerCase().includes(query),
          }}
          pagination={
            meta
              ? {
                  page: meta.page,
                  totalPages: meta.total_pages,
                  onPage: (page) => setFilters((current) => ({ ...current, page })),
                  isFetching,
                }
              : undefined
          }
        />
      </Card>

      <Periods />
    </div>
  )
}
